import React, { useEffect, useMemo, useState } from 'react';
import { Table, InputNumber, Button, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { CheckOutlined, CloseOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';

interface LabItem {
  key?: string;
  divisionLabel: string;
  typeLabel: string;
  mainServiceLabel: string;
  subServiceLabel: string;
  qty: number;
  uom: string;
  unit_price: number;
  total: number;
}

interface HeaderRow {
  key: string;
  isHeader: true;
  typeLabel: string;
}

interface DataRow extends LabItem {
  isHeader: false;
}

type TableRow = HeaderRow | DataRow;

interface LabItemTableProps {
  data: LabItem[];
  onRemove?: (key: string) => void;
  onChange?: (updatedData: LabItem[]) => void;
  editable?: boolean;
  type?: 'edit' | 'create' | 'view';
  onQtyChange?: (key: string, qty: number) => Promise<void>;
}

const LabItemTable: React.FC<LabItemTableProps> = ({ data, onRemove, onChange, editable, type, onQtyChange }) => {
  const [editingQty, setEditingQty] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<number>(0);
  const [loading, setLoading] = useState<string | null>(null);

  // Assign unique keys if not already present
  const dataWithKeys = useMemo(() => {
    return data.map((item, index) => ({
      ...item,
      key: item.key || `${item.typeLabel}-${item.mainServiceLabel}-${index}`,
    }));
  }, [data]);

  // Group items by typeLabel and create a flattened list with headers
  const flattenedData = useMemo(() => {
    const grouped = dataWithKeys.reduce<Record<string, LabItem[]>>((acc, item) => {
      acc[item.typeLabel] = acc[item.typeLabel] || [];
      acc[item.typeLabel].push(item);
      return acc;
    }, {});

    const result: TableRow[] = [];
    Object.entries(grouped).forEach(([typeLabel, items]) => {
      result.push({ key: `header-${typeLabel}`, isHeader: true, typeLabel });
      items.forEach((item) => result.push({ ...item, isHeader: false }));
    });
    return result;
  }, [dataWithKeys]);

  const handleQtyEditStart = (key: string, currentQty: number) => {
    setEditingQty(key);
    setEditValue(currentQty);
  };

  const handleQtyEditCancel = () => {
    setEditingQty(null);
    setEditValue(0);
  };

  const handleQtyEditSave = async () => {
    if (!editingQty || !onQtyChange) return;

    if (editValue <= 0) {
      message.error('Quantity must be greater than 0');
      return;
    }

    try {
      setLoading(editingQty);
      await onQtyChange(editingQty, editValue);
      setEditingQty(null);
      setEditValue(0);
      message.success('Quantity updated successfully');
    } catch (error) {
      message.error('Failed to update quantity. Please try again.');
    } finally {
      setLoading(null);
    }
  };

  const renderQtyField = (qty: number, record: DataRow) => {
    const isEditing = editingQty === record.key;
    const isLoading = loading === record.key;

    if (editable && type !== 'view') {
      if (isEditing) {
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <InputNumber
              min={1}
              value={editValue}
              onChange={(newValue) => setEditValue(newValue || 0)}
              style={{ width: 80 }}
              size="small"
              disabled={isLoading}
            />
            <Button
              type="text"
              icon={<CheckOutlined />}
              size="small"
              onClick={handleQtyEditSave}
              loading={isLoading}
              style={{ color: '#52c41a' }}
            />
            <Button
              type="text"
              icon={<CloseOutlined />}
              size="small"
              onClick={handleQtyEditCancel}
              disabled={isLoading}
              style={{ color: '#ff4d4f' }}
            />
          </div>
        );
      }

      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span>{qty}</span>
          <Button
            type="text"
            icon={<EditOutlined />}
            size="small"
            onClick={() => handleQtyEditStart(record.key!, qty)}
            style={{ color: '#1890ff' }}
          />
        </div>
      );
    }

    return qty;
  };

  const columns: ColumnsType<TableRow> = [
    {
      title: 'Division',
      dataIndex: 'divisionLabel',
      render: (_: any, record: TableRow) => (record.isHeader ? { props: { colSpan: 0 } } : record.divisionLabel),
    },
    {
      title: 'Main Service',
      dataIndex: 'mainServiceLabel',
      render: (text: string, record: TableRow) =>
        record.isHeader
          ? {
              children: <strong>{record.typeLabel}s</strong>,
              props: { colSpan: editable ? 8 : 7 },
            }
          : record.mainServiceLabel,
    },
    {
      title: 'Sub Service',
      dataIndex: 'subServiceLabel',
      render: (_: any, record: TableRow) => (record.isHeader ? { props: { colSpan: 0 } } : record.subServiceLabel),
    },
    {
      title: 'QTY',
      dataIndex: 'qty',
      render: (_: any, record: TableRow) => {
        if (record.isHeader) return { props: { colSpan: 0 } };
        return renderQtyField(record.qty, record as DataRow);
      },
    },
    {
      title: 'UOM',
      dataIndex: 'uom',
      render: (_: any, record: TableRow) => {
        if (record.isHeader) return { props: { colSpan: 0 } };

        const isCurrentlyLoading = loading === record.key;
        return isCurrentlyLoading ? 'Updating...' : record.uom;
      },
    },
    {
      title: 'Unit Price',
      dataIndex: 'unit_price',
      render: (value: any, record: TableRow) => {
        if (record.isHeader) return { props: { colSpan: 0 } };

        const isCurrentlyLoading = loading === record.key;
        if (isCurrentlyLoading) return 'Updating...';

        const numValue =
          typeof record.unit_price === 'string' ? parseFloat(record.unit_price) : Number(record.unit_price);
        return isNaN(numValue) ? '-' : `LKR ${numValue.toLocaleString()}/=`;
      },
    },
    {
      title: 'Sub Total',
      dataIndex: 'total',
      render: (_: any, record: TableRow) => {
        if (record.isHeader) return { props: { colSpan: 0 } };

        const isCurrentlyLoading = loading === record.key;
        if (isCurrentlyLoading) return 'Updating...';

        const qty = Number(record.qty || 0);
        const unit =
          typeof record.unit_price === 'string' ? parseFloat(record.unit_price) : Number(record.unit_price || 0);
        const subtotal = qty * unit;
        return isNaN(subtotal) ? '-' : `LKR ${subtotal.toLocaleString()}/=`;
      },
    },
    ...(editable
      ? [
          {
            title: 'Actions',
            dataIndex: 'actions',
            render: (_: any, record: TableRow) =>
              record.isHeader ? (
                { props: { colSpan: 0 } }
              ) : (
                <Button danger icon={<DeleteOutlined />} size="small" onClick={() => onRemove?.(record.key!)} />
              ),
          },
        ]
      : []),
  ];

  return <Table columns={columns} dataSource={flattenedData} pagination={false} rowKey="key" bordered />;
};

export default LabItemTable;
